import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";

interface Account {
  id: string;
  name: string;
  parent_account_id: string | null;
}

export const AccountManager = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [newAccountName, setNewAccountName] = useState("");
  const [parentAccountId, setParentAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchAccounts = async () => {
    try {
      const { data, error } = await supabase
        .from("accounts")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;
      setAccounts(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAccountName.trim()) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("accounts").insert({
        user_id: user.id,
        name: newAccountName.trim(),
        parent_account_id: parentAccountId,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Account added successfully",
      });
      setNewAccountName("");
      setParentAccountId(null);
      fetchAccounts();
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (id: string) => {
    try {
      const { error } = await supabase.from("accounts").delete().eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Account deleted",
      });
      fetchAccounts();
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manage Accounts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleAddAccount} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="accountName">Account Name</Label>
              <Input
                id="accountName"
                type="text"
                placeholder="e.g., Holiday Account, Savings"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parentAccount">Parent Account (Optional)</Label>
              <Select value={parentAccountId || "none"} onValueChange={(value) => setParentAccountId(value === "none" ? null : value)}>
                <SelectTrigger id="parentAccount">
                  <SelectValue placeholder="None (Root Account)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Root Account)</SelectItem>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Account
          </Button>
        </form>

        <div className="space-y-2">
          {accounts.length === 0 ? (
            <p className="text-center text-muted-foreground py-4 text-sm">
              No accounts yet. Add your first account above!
            </p>
          ) : (
            accounts.map((account) => {
              const parentAccount = accounts.find(a => a.id === account.parent_account_id);
              return (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{account.name}</span>
                    {parentAccount && (
                      <span className="text-sm text-muted-foreground">
                        ↳ under {parentAccount.name}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteAccount(account.id)}
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};